import { BlockComponent } from "../../../framework/src/BlockComponent";
import { runEngine } from "../../../framework/src/RunEngine";
import { Message } from "../../../framework/src/Message";
import { IBlock } from "../../../framework/src/IBlock";
import MessageEnum, {
  getName,
} from "../../../framework/src/Messages/MessageEnum";

// Customizable Area Start
import { makeApiMessage as MakeApiMessage } from "../../../components/src/common";

const configJSON = require("./StoreLocatorConfig");

export interface IStoreTimeForm {
  day: number;
  opening_time: string;
  closing_time: string;
  is_closed: boolean;
}

export interface IStoreForm {
  name_en: string;
  name_ar: string;
  address_en: string;
  address_ar: string;
  latitude: string;
  longitude: string;
  phone: string;
  rating: string;
  total_ratings: string;
  google_place_id: string;
  google_maps_url: string;
  is_active: boolean;
  position: string;
  images: File[];
  store_times: IStoreTimeForm[];
}

const defaultStoreTimes: IStoreTimeForm[] = [
  { day: 0, opening_time: "08:00", closing_time: "22:00", is_closed: false },
  { day: 1, opening_time: "08:00", closing_time: "22:00", is_closed: false },
  { day: 2, opening_time: "08:00", closing_time: "22:00", is_closed: false },
  { day: 3, opening_time: "08:00", closing_time: "22:00", is_closed: false },
  { day: 4, opening_time: "08:00", closing_time: "22:00", is_closed: false },
  { day: 5, opening_time: "14:00", closing_time: "22:00", is_closed: false },
  { day: 6, opening_time: "", closing_time: "", is_closed: true },
];
// Customizable Area End

export interface Props {
  // Customizable Area Start
  navigation: {
    navigate: Function;
    getParam: Function;
    goBack: Function;
  };
  id: string;
  // Customizable Area End
}

export interface S {
  // Customizable Area Start
  form: IStoreForm;
  imagePreviews: string[];
  isSubmitting: boolean;
  errors: Record<string, string>;
  // Customizable Area End
}

export interface SS {}

class StoreCreateController extends BlockComponent<Props, S, SS> {
  // Customizable Area Start
  createStoreMessageId: string = "";
  // Customizable Area End

  constructor(props: Props) {
    super(props);
    // Customizable Area Start
    this.subScribedMessages = [
      getName(MessageEnum.RestAPIResponceMessage),
      getName(MessageEnum.RestAPIResponceSuccessMessage),
      getName(MessageEnum.RestAPIResponceDataMessage),
    ];

    this.receive = this.receive.bind(this);
    runEngine.attachBuildingBlock(this as IBlock, this.subScribedMessages);
    // Customizable Area End
  }

  async receive(from: string, message: Message) {
    // Customizable Area Start
    const requestId = message.getData(
      getName(MessageEnum.RestAPIResponceDataMessage)
    );

    const successResponse = message.getData(
      getName(MessageEnum.RestAPIResponceSuccessMessage)
    );

    if (requestId === this.createStoreMessageId) {
      this.setState({ isSubmitting: false });
      if (successResponse && successResponse.data) {
        this.props.navigation.navigate("MobileStoreList");
      }
    }
    // Customizable Area End
  }

  // Customizable Area Start
  getInitialForm = (): IStoreForm => ({
    name_en: "",
    name_ar: "",
    address_en: "",
    address_ar: "",
    latitude: "",
    longitude: "",
    phone: "",
    rating: "",
    total_ratings: "",
    google_place_id: "",
    google_maps_url: "",
    is_active: true,
    position: "0",
    images: [],
    store_times: [...defaultStoreTimes],
  });

  handleFormChange = (field: keyof IStoreForm, value: any) => {
    this.setState({ form: { ...this.state.form, [field]: value } });
  };

  handleStoreTimeChange = (
    index: number,
    field: keyof IStoreTimeForm,
    value: any
  ) => {
    const storeTimes = [...this.state.form.store_times];
    storeTimes[index] = { ...storeTimes[index], [field]: value };

    if (field === "is_closed" && value) {
      storeTimes[index].opening_time = "";
      storeTimes[index].closing_time = "";
    }

    this.setState({ form: { ...this.state.form, store_times: storeTimes } });
  };

  handleImagesChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files) return;

    const newFiles = Array.from(files);
    const allImages = [...this.state.form.images, ...newFiles];

    const previews = [...this.state.imagePreviews];
    newFiles.forEach((file) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        previews.push(e.target?.result as string);
        this.setState({ imagePreviews: [...previews] });
      };
      reader.readAsDataURL(file);
    });

    this.setState({ form: { ...this.state.form, images: allImages } });
  };

  handleRemoveImage = (index: number) => {
    const images = [...this.state.form.images];
    const previews = [...this.state.imagePreviews];
    images.splice(index, 1);
    previews.splice(index, 1);
    this.setState({
      form: { ...this.state.form, images },
      imagePreviews: previews,
    });
  };

  validateForm = (): boolean => {
    const errors: Record<string, string> = {};
    const { form } = this.state;

    if (!form.name_en.trim()) errors.name_en = "English name is required";
    if (!form.name_ar.trim()) errors.name_ar = "Arabic name is required";
    if (!form.address_en.trim())
      errors.address_en = "English address is required";
    if (!form.address_ar.trim())
      errors.address_ar = "Arabic address is required";

    this.setState({ errors });
    return Object.keys(errors).length === 0;
  };

  handleSubmit = () => {
    if (!this.validateForm()) return;

    this.setState({ isSubmitting: true });

    const { form } = this.state;
    const formData = new FormData();

    formData.append("name_en", form.name_en);
    formData.append("name_ar", form.name_ar);
    formData.append("address_en", form.address_en);
    formData.append("address_ar", form.address_ar);
    formData.append("is_active", String(form.is_active));
    formData.append("position", form.position || "0");

    if (form.latitude) formData.append("latitude", form.latitude);
    if (form.longitude) formData.append("longitude", form.longitude);
    if (form.phone) formData.append("phone", form.phone);
    if (form.rating) formData.append("rating", form.rating);
    if (form.total_ratings)
      formData.append("total_ratings", form.total_ratings);
    if (form.google_place_id)
      formData.append("google_place_id", form.google_place_id);
    if (form.google_maps_url)
      formData.append("google_maps_url", form.google_maps_url);

    form.images.forEach((file) => {
      formData.append("images[]", file);
    });

    form.store_times.forEach((time, index) => {
      formData.append(
        `store_times_attributes[${index}][day]`,
        String(time.day)
      );
      formData.append(
        `store_times_attributes[${index}][opening_time]`,
        time.opening_time
      );
      formData.append(
        `store_times_attributes[${index}][closing_time]`,
        time.closing_time
      );
      formData.append(
        `store_times_attributes[${index}][is_closed]`,
        String(time.is_closed)
      );
    });

    const requestMessage = MakeApiMessage({
      url: configJSON.ApiUrls.storesList,
      method: "POST",
      body: formData,
      isHeader: true,
    });

    this.createStoreMessageId = requestMessage.messageId;
    runEngine.sendMessage(requestMessage.id, requestMessage);
  };

  handleCancel = () => {
    this.props.navigation.navigate("MobileStoreList");
  };
  // Customizable Area End
}

export default StoreCreateController;
